import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import {
  getActualCosts,
  type ProductionCostRecord,
} from '@/services/costingService';
import CalculateCostModal from '../orders/components/CalculateCostModal';
import { ActualCostDetailsModal } from '../variance/components/ActualCostDetailsModal';
import { formatINR } from '@/utils/format';
import { useWarehouseStore } from '@/stores/warehouseStore';

function formatCurrency(value: number): string {
  return formatINR(value || 0);
}

export default function ActualCostPage() {
  const navigate = useNavigate();
  const { selectedWarehouseId } = useWarehouseStore();
  const [loading, setLoading] = useState(true);
  const [actualCosts, setActualCosts] = useState<ProductionCostRecord[]>([]);
  const [selectedCost, setSelectedCost] = useState<ProductionCostRecord | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getActualCosts('', 'all', selectedWarehouseId);
      if (res && res.success && Array.isArray(res.data)) {
        setActualCosts(res.data.filter((r) => Number(r.totalCost) > 0));
      } else {
        setActualCosts([]);
      }
    } catch (err) {
      console.error("Failed to load actual costs:", err);
      setActualCosts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedWarehouseId]);

  const handleSaveCost = (newRecord: ProductionCostRecord) => {
    setShowCalcModal(false);
    showToast(`Actual Cost calculated and saved for ${newRecord.productionOrderNumber}`);
    loadData();
  };

  const handleOpenDetail = (actual: ProductionCostRecord) => {
    setSelectedCost(actual);
    setShowDetailModal(true);
  };

  const summary = useMemo(() => {
    const totalActCost = actualCosts.reduce((s, c) => s + c.totalCost, 0);
    const totalQty = actualCosts.reduce((s, c) => s + c.producedQty, 0);
    const totalActMaterial = actualCosts.reduce((s, c) => s + c.materialCost, 0);
    const totalActLabourOH = actualCosts.reduce((s, c) => s + c.labourCost + c.overheadCost, 0);

    return { totalActCost, totalQty, totalActMaterial, totalActLabourOH };
  }, [actualCosts]);

  return (
    <AppLayout>
      <div className="space-y-6 p-4 md:p-6 max-w-[1700px] mx-auto min-h-screen bg-[#f8fafc]/40">
        {/* Toast Notification */}
        {toast && (
          <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-xl flex items-center gap-2">
            <i className="ri-checkbox-circle-fill text-green-400 text-base" />
            <span>{toast}</span>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/manufacturing/costing')}
              className="p-2 text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer shadow-2xs transition-colors"
              title="Back to Dashboard"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">Actual Cost</h1>
              <p className="text-xs text-slate-500 mt-0.5 font-medium">Recorded cost from actual production entries and operator logs</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCalcModal(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 cursor-pointer whitespace-nowrap shadow-2xs"
            >
              <i className="ri-calculator-line text-sm" />
              Calculate Actual Cost
            </button>
            <button
              onClick={loadData}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer whitespace-nowrap shadow-2xs"
            >
              <i className="ri-refresh-line text-sm" />
              Refresh
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-2xs">
            <div className="text-xs text-slate-400 font-medium mb-1">Total Actual Cost</div>
            <div className="text-xl font-bold text-slate-900">{formatCurrency(summary.totalActCost)}</div>
            <div className="mt-1 text-[11px] text-slate-400 font-medium">
              Across {actualCosts.length} production order{actualCosts.length !== 1 ? 's' : ''}
            </div>
          </div>

          <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-2xs">
            <div className="text-xs text-slate-400 font-medium mb-1">Total Produced Qty</div>
            <div className="text-xl font-bold text-slate-900">{summary.totalQty} Pcs</div>
            <div className="mt-1 text-[11px] text-slate-400 font-medium">
              Actual production quantity
            </div>
          </div>

          <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-2xs">
            <div className="text-xs text-slate-400 font-medium mb-1">Total Actual Material</div>
            <div className="text-xl font-bold text-slate-900">{formatCurrency(summary.totalActMaterial)}</div>
            <div className="mt-1 text-[11px] text-slate-400 font-medium">
              Actual material consumed
            </div>
          </div>

          <div className="bg-white border border-slate-200/90 rounded-xl p-4 shadow-2xs">
            <div className="text-xs text-slate-400 font-medium mb-1">Total Actual Labour + OH</div>
            <div className="text-xl font-bold text-slate-900">{formatCurrency(summary.totalActLabourOH)}</div>
            <div className="mt-1 text-[11px] text-slate-400 font-medium">
              Labour + Overhead actual
            </div>
          </div>
        </div>

        {/* Actual Cost Table */}
        <div className="bg-white border border-slate-200/90 rounded-xl overflow-hidden shadow-2xs">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Actual Cost Records</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-200 text-slate-500 font-semibold select-none text-[11px]">
                  <th className="py-3 px-3.5 text-left whitespace-nowrap">PO Number</th>
                  <th className="py-3 px-3.5 text-left whitespace-nowrap">Product</th>
                  <th className="py-3 px-3 text-center whitespace-nowrap">Qty</th>
                  <th className="py-3 px-3.5 text-right whitespace-nowrap">Act Material</th>
                  <th className="py-3 px-3.5 text-right whitespace-nowrap">Act Labour</th>
                  <th className="py-3 px-3.5 text-right whitespace-nowrap">Act Overhead</th>
                  <th className="py-3 px-3.5 text-right whitespace-nowrap font-bold text-slate-700">Act Total</th>
                  <th className="py-3 px-3.5 text-right whitespace-nowrap">Cost / Unit</th>
                  <th className="py-3 px-3.5 text-center whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {actualCosts.map((actual) => (
                  <tr key={actual.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-3.5 text-slate-900 font-bold whitespace-nowrap">{actual.productionOrderNumber}</td>
                    <td className="py-3 px-3.5 whitespace-nowrap">
                      <div className="font-bold text-slate-900">{actual.productName}</div>
                      {actual.variantName ? <span className="block text-slate-400 text-[11px]">{actual.variantName}</span> : null}
                    </td>
                    <td className="py-3 px-3 text-center text-slate-700 whitespace-nowrap">{actual.producedQty} {actual.unit}</td>
                    <td className="py-3 px-3.5 text-right text-slate-700 whitespace-nowrap">{formatCurrency(actual.materialCost)}</td>
                    <td className="py-3 px-3.5 text-right text-slate-700 whitespace-nowrap">{formatCurrency(actual.labourCost)}</td>
                    <td className="py-3 px-3.5 text-right text-slate-700 whitespace-nowrap">{formatCurrency(actual.overheadCost)}</td>
                    <td className="py-3 px-3.5 text-right font-bold text-slate-900 whitespace-nowrap">{formatCurrency(actual.totalCost)}</td>
                    <td className="py-3 px-3.5 text-right text-slate-700 whitespace-nowrap">{formatCurrency(actual.costPerUnit)}/unit</td>
                    <td className="py-3 px-3.5 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => handleOpenDetail(actual)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer shadow-2xs transition-colors"
                          title="View Actual Cost Details"
                        >
                          <i className="ri-eye-line text-xs" />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {actualCosts.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-10 text-center text-slate-400">
                      {loading ? 'Loading actual cost records...' : 'No actual cost records found in database.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal: Actual Cost Details (Exact replica of media_1791437074079.png) */}
        {showDetailModal && selectedCost && (
          <ActualCostDetailsModal
            open={showDetailModal}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedCost(null);
            }}
            costRecord={selectedCost}
          />
        )}

        {/* Calculate Cost Modal */}
        {showCalcModal && (
          <CalculateCostModal
            initialMethod="ACTUAL"
            onClose={() => setShowCalcModal(false)}
            onSave={handleSaveCost}
          />
        )}
      </div>
    </AppLayout>
  );
}